import * as React from 'react';
import Box from '@mui/material/Box';
import ICoordinate from '../../../../map/ICoordinate';
import { isGoogleMapsAvailable } from '../state/useAddressLocation';

const ZOOM_WITHOUT_LOCATION = 11;
const ZOOM_WITH_LOCATION = 16;

const startingPointBySite: Record<string, ICoordinate> = {
    zentuvo: { latitude: 60.169857, longitude: 24.938379 },
    se360: { latitude: 57.731942, longitude: 12.00692 }
};

type LatLng = { lat: number; lng: number };
type MapMouseEvent = { latLng: { lat: () => number; lng: () => number } };
type GoogleMap = {
    addListener: (eventName: string, listener: (event: MapMouseEvent) => void) => void;
    panTo: (position: LatLng) => void;
    setZoom: (zoom: number) => void;
};
type GoogleMarker = {
    addListener: (eventName: string, listener: (event: MapMouseEvent) => void) => void;
    setPosition: (position: LatLng) => void;
    setMap: (map: GoogleMap | null) => void;
};

type AddressMapProps = {
    location: ICoordinate | null;
    proposedLocation: ICoordinate | null;
    onPick: (location: ICoordinate) => void;
};

function toCoordinate(event: MapMouseEvent): ICoordinate {
    return { latitude: event.latLng.lat(), longitude: event.latLng.lng() };
}

export function AddressMap({ location, proposedLocation, onPick }: AddressMapProps) {
    let containerRef = React.useRef<HTMLDivElement>(null);
    let mapRef = React.useRef<GoogleMap | null>(null);
    let markerRef = React.useRef<GoogleMarker | null>(null);
    let startingLocationRef = React.useRef(location);
    let onPickRef = React.useRef(onPick);
    onPickRef.current = onPick;

    let shownLocation = proposedLocation || location;
    let latitude = shownLocation ? shownLocation.latitude : null;
    let longitude = shownLocation ? shownLocation.longitude : null;

    React.useEffect(() => {
        if (!containerRef.current || !isGoogleMapsAvailable()) {
            return;
        }

        let maps = window.google.maps;
        let startingLocation = startingLocationRef.current;
        let startingPoint =
            startingLocation ||
            startingPointBySite[window.appConfig.site] ||
            startingPointBySite.se360;
        let map: GoogleMap = new maps.Map(containerRef.current, {
            center: { lat: startingPoint.latitude, lng: startingPoint.longitude },
            zoom: startingLocation ? ZOOM_WITH_LOCATION : ZOOM_WITHOUT_LOCATION,
            streetViewControl: false,
            mapTypeId: maps.MapTypeId.ROADMAP
        });
        map.addListener('click', (event) => onPickRef.current(toCoordinate(event)));
        mapRef.current = map;
    }, []);

    React.useEffect(() => {
        let map = mapRef.current;
        if (!map) {
            return;
        }

        if (latitude === null || longitude === null) {
            if (markerRef.current) {
                markerRef.current.setMap(null);
                markerRef.current = null;
            }
            return;
        }

        let position = { lat: latitude, lng: longitude };
        if (markerRef.current) {
            markerRef.current.setPosition(position);
        } else {
            let marker: GoogleMarker = new window.google.maps.Marker({
                position,
                map,
                draggable: true
            });
            marker.addListener('dragend', (event) => onPickRef.current(toCoordinate(event)));
            markerRef.current = marker;
        }
        map.panTo(position);
        map.setZoom(ZOOM_WITH_LOCATION);
    }, [latitude, longitude]);

    return <Box ref={containerRef} sx={{ width: '100%', height: '100%', minHeight: '300px' }} />;
}
